// 2FA rework (2026-08-17) — the settings surface for two-factor auth.
// Rendered on /account/preferences. Opt-in for every user; REQUIRED for
// admins before /admin/* opens up (AdminUser extractor, migration 0072).
//
// Three sub-states:
//   * not enrolled            → "Enable" → QR + secret → confirm code →
//                               backup codes revealed ONCE → done.
//   * enrolled                → status + regenerate backup codes (needs a
//                               current code) + disable (password + code).
//
// The enroll/start response carries the backup codes, but they only become
// VALID once confirm succeeds — so the UI holds them in memory during the
// confirm step and reveals them after, never before.

import { useState, type FormEvent } from "react";
import toast from "react-hot-toast";
import { QRCodeSVG } from "qrcode.react";
import { Button, Input, Label } from "@greenhold/components";

import { useAccountMe } from "../admin/useAccountMe";
import {
  useRegenerateBackupCodes,
  useTwoFactorDisable,
  useTwoFactorEnrollConfirm,
  useTwoFactorEnrollStart,
  useTwoFactorStatus,
  type EnrollStartResponse,
  type TwoFactorError,
} from "./useTwoFactor";

function BackupCodeList({ codes }: { codes: string[] }) {
  return (
    <div className="rounded-md border border-brand-gold-500/40 bg-muted/30 p-4">
      <p className="font-body text-sm font-medium m-0 mb-2">
        Backup codes — save these somewhere safe. Each works once, and they
        are shown only now.
      </p>
      <ul className="grid grid-cols-2 gap-1 font-mono text-sm m-0 p-0 list-none">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
    </div>
  );
}

export function TwoFactorSettingsSection() {
  const me = useAccountMe();
  const status = useTwoFactorStatus();

  const enrollStart = useTwoFactorEnrollStart();
  const enrollConfirm = useTwoFactorEnrollConfirm();
  const disable = useTwoFactorDisable();
  const regenerate = useRegenerateBackupCodes();

  // Enrollment-in-progress state (QR shown, confirm pending).
  const [pendingEnrollment, setPendingEnrollment] =
    useState<EnrollStartResponse | null>(null);
  const [confirmCode, setConfirmCode] = useState("");
  // Codes revealed once — either right after enroll-confirm or after a
  // regenerate.
  const [revealedCodes, setRevealedCodes] = useState<string[] | null>(null);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [regenerateCode, setRegenerateCode] = useState("");
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  const isAdmin = me.data?.data.is_admin ?? false;

  if (status.isPending) {
    return (
      <section className="rounded-lg border border-border bg-card/40 p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">
          Two-factor authentication
        </h2>
        <p className="font-body text-sm opacity-70 m-0">Loading…</p>
      </section>
    );
  }

  const enrolled = status.data?.enrolled ?? false;

  const handleEnrollStart = async () => {
    try {
      const out = await enrollStart.mutateAsync();
      setPendingEnrollment(out);
      setConfirmCode("");
      setRevealedCodes(null);
    } catch (err) {
      toast.error((err as TwoFactorError).message);
    }
  };

  const handleEnrollConfirm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pendingEnrollment) return;
    try {
      await enrollConfirm.mutateAsync({ code: confirmCode.trim() });
      setRevealedCodes(pendingEnrollment.backup_codes);
      setPendingEnrollment(null);
      toast.success("Two-factor authentication enabled.");
    } catch (err) {
      const e = err as TwoFactorError;
      toast.error(
        e.code === "TWO_FACTOR_INVALID"
          ? "That code didn't match — try the next one from your app."
          : e.message,
      );
    }
  };

  const handleDisable = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await disable.mutateAsync({
        password: disablePassword,
        code: disableCode.trim(),
      });
      setDisableOpen(false);
      setDisablePassword("");
      setDisableCode("");
      setRevealedCodes(null);
      toast.success("Two-factor authentication disabled.");
    } catch (err) {
      const e = err as TwoFactorError;
      toast.error(
        e.code === "TWO_FACTOR_INVALID"
          ? "Password or code didn't match."
          : e.message,
      );
    }
  };

  const handleRegenerate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const out = await regenerate.mutateAsync({
        code: regenerateCode.trim(),
      });
      setRevealedCodes(out.backup_codes);
      setRegenerateOpen(false);
      setRegenerateCode("");
      toast.success("New backup codes generated — the old ones are dead.");
    } catch (err) {
      const e = err as TwoFactorError;
      toast.error(
        e.code === "TWO_FACTOR_INVALID"
          ? "That code didn't match."
          : e.message,
      );
    }
  };

  return (
    <section
      className="rounded-lg border border-border bg-card/40 p-6 space-y-4"
      data-testid="two-factor-settings"
    >
      <h2 className="font-heading text-lg font-semibold">
        Two-factor authentication
      </h2>

      {!enrolled && !pendingEnrollment ? (
        <>
          <p className="font-body text-sm opacity-80 leading-relaxed m-0">
            Add a second factor: a 6-digit code from an authenticator app
            (1Password, Google Authenticator, Aegis…) asked at every sign-in.
          </p>
          {isAdmin ? (
            <p
              className="font-body text-sm text-brand-gold-300 m-0"
              data-testid="two-factor-admin-nudge"
            >
              Your account has admin access — the admin area stays locked
              until two-factor authentication is set up.
            </p>
          ) : null}
          <Button
            type="button"
            variant="primary"
            onClick={handleEnrollStart}
            disabled={enrollStart.isPending}
            aria-busy={enrollStart.isPending}
            data-testid="two-factor-enable"
          >
            {enrollStart.isPending ? "Preparing…" : "Enable two-factor auth"}
          </Button>
        </>
      ) : null}

      {pendingEnrollment ? (
        <div className="space-y-4" data-testid="two-factor-enroll-flow">
          <p className="font-body text-sm opacity-80 leading-relaxed m-0">
            Scan the QR code with your authenticator app, then enter the
            6-digit code it shows to finish.
          </p>
          <div className="bg-white p-3 rounded w-fit">
            <QRCodeSVG value={pendingEnrollment.otpauth_url} size={168} />
          </div>
          <p className="font-body text-xs opacity-70 m-0">
            Can't scan? Enter this key manually:{" "}
            <span className="font-mono break-all">
              {pendingEnrollment.secret}
            </span>
          </p>
          <form onSubmit={handleEnrollConfirm} className="flex items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="two-factor-confirm-code">Code from app</Label>
              <Input
                id="two-factor-confirm-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                disabled={enrollConfirm.isPending}
                className="w-36"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              disabled={
                enrollConfirm.isPending || confirmCode.trim().length === 0
              }
              aria-busy={enrollConfirm.isPending}
              data-testid="two-factor-confirm"
            >
              {enrollConfirm.isPending ? "Confirming…" : "Confirm"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPendingEnrollment(null)}
              disabled={enrollConfirm.isPending}
            >
              Cancel
            </Button>
          </form>
        </div>
      ) : null}

      {revealedCodes ? <BackupCodeList codes={revealedCodes} /> : null}

      {enrolled ? (
        <div className="space-y-4">
          <p className="font-body text-sm opacity-80 m-0">
            ✅ Enabled. {status.data?.backup_codes_remaining ?? 0} backup
            codes remaining.
          </p>

          {!regenerateOpen && !disableOpen ? (
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setRegenerateOpen(true)}
                data-testid="two-factor-regenerate-open"
              >
                Regenerate backup codes
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDisableOpen(true)}
                data-testid="two-factor-disable-open"
              >
                Disable…
              </Button>
            </div>
          ) : null}

          {regenerateOpen ? (
            <form onSubmit={handleRegenerate} className="flex items-end gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="two-factor-regenerate-code">
                  Current code
                </Label>
                <Input
                  id="two-factor-regenerate-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={regenerateCode}
                  onChange={(e) => setRegenerateCode(e.target.value)}
                  disabled={regenerate.isPending}
                  className="w-36"
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                disabled={
                  regenerate.isPending || regenerateCode.trim().length === 0
                }
                aria-busy={regenerate.isPending}
              >
                {regenerate.isPending ? "Working…" : "Regenerate"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setRegenerateOpen(false)}
                disabled={regenerate.isPending}
              >
                Cancel
              </Button>
            </form>
          ) : null}

          {disableOpen ? (
            <form onSubmit={handleDisable} className="space-y-3">
              <p className="font-body text-sm opacity-80 m-0">
                Disabling requires your password AND a current code — backup
                codes are not accepted here.
              </p>
              <div className="flex flex-col gap-1.5 max-w-xs">
                <Label htmlFor="two-factor-disable-password">Password</Label>
                <Input
                  id="two-factor-disable-password"
                  type="password"
                  autoComplete="current-password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  disabled={disable.isPending}
                />
              </div>
              <div className="flex flex-col gap-1.5 max-w-xs">
                <Label htmlFor="two-factor-disable-code">Current code</Label>
                <Input
                  id="two-factor-disable-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  disabled={disable.isPending}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    disable.isPending ||
                    disablePassword.length === 0 ||
                    disableCode.trim().length === 0
                  }
                  aria-busy={disable.isPending}
                  data-testid="two-factor-disable-submit"
                >
                  {disable.isPending ? "Disabling…" : "Disable two-factor"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDisableOpen(false)}
                  disabled={disable.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
